import React from 'react';
import { 
  Users, 
  Building, 
  Tag,
  User,
  Music,
  Trophy,
  MapPin,
  Clock,
  Star,
  Ban,
  RotateCcw
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { usePolling } from '../context/PollingContext';
import { CardTitle } from './UniversalText';
import { useUniversalTextSizing, getUniversalTextConfig } from '../utils/cardTitleSizing';

interface RoutineMetadataCardProps {
  currentRoutine: any;
  onScratchRoutine?: () => void;
  onUnscratchRoutine?: () => void;
  className?: string;
}

const RoutineMetadataCard: React.FC<RoutineMetadataCardProps> = ({ 
  currentRoutine, 
  onScratchRoutine,
  onUnscratchRoutine,
  className = "" 
}) => {
  const { theme } = useTheme();
  const { eventTimezone } = usePolling();
  const isDarkMode = theme === 'dark';
  
  // Use universal sizing to match LiveScoreMonitor responsiveness
  const { textSize } = useUniversalTextSizing();
  const universalConfig = getUniversalTextConfig(textSize);

  if (!currentRoutine) {
    return (
      <div className={`${className} flex items-center justify-center h-full`}>
        <div className={`text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          <Music className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No routine selected</p>
        </div>
      </div>
    );
  }

  // Helper function to get submission routine title (prefers lastSubmitted, then first)
  const getSubmissionRoutineTitle = (routine: any): string | undefined => {
    if (!routine?.parentRoutine?.submissionRoutines || 
        !Array.isArray(routine.parentRoutine.submissionRoutines) || 
        routine.parentRoutine.submissionRoutines.length === 0) {
      // Fallback to parentRoutine.title if no submission routines exist
      return routine?.parentRoutine?.title;
    }
    
    // Prefer the routine with lastSubmitted: true
    const lastSubmitted = routine.parentRoutine.submissionRoutines.find((sr: any) => sr.lastSubmitted);
    const submissionRoutine = lastSubmitted || routine.parentRoutine.submissionRoutines[0];
    
    return submissionRoutine?.title || routine?.parentRoutine?.title;
  };

  // Helper functions to extract metadata (similar to EnhancedRoutineViewer)
  const getPerformerName = (routine: any) => {
    if (routine.performerName) return routine.performerName;
    
    // Try to get from HitchKick data structure first
    const hitchkickDancers = routine.parentRoutine?.submissionRoutines?.[0]?.routineDancers;
    if (hitchkickDancers && hitchkickDancers.length > 0) {
      if (hitchkickDancers.length === 1) {
        const dancer = hitchkickDancers[0].rosterDancers;
        if (dancer && dancer.firstName && dancer.lastName) {
          return `${dancer.firstName} ${dancer.lastName}`;
        }
      } else {
        // Group routine - show nothing (empty string)
        return '';
      }
    }
    
    // Fallback to original enhanced data structure
    if (routine.parentRoutine?.routineDancers?.length > 0) {
      const dancers = routine.parentRoutine.routineDancers;
      if (dancers.length === 1) {
        const dancer = dancers[0].rosterDancers;
        return `${dancer.firstName} ${dancer.lastName}`;
      } else {
        // Group routine - show nothing (empty string)
        return '';
      }
    }
    
    // Only return routine name as performer if we have no dancer data at all
    // This prevents "Midas (Midas)" for group routines
    return '';
  };

  const getStudioName = (routine: any) => {
    if (routine.studioName) return routine.studioName;
    
    // Try to get from enhanced data structure
    try {
      return routine.parentRoutine?.submissionRoutines?.[0]?.registrations?.studios?.businessName || 'Unknown Studio';
    } catch (error) {
      return 'Unknown Studio';
    }
  };

  const getStudioCode = (routine: any) => {
    return routine.studioCode || '';
  };

  const getCombinedCategoryInfo = (routine: any) => {
    // Try basic fields first
    let category = routine.category || '';
    let level = routine.ageGroup || routine.level || '';
    let division = routine.division || '';

    // Try enhanced data structure if basic fields are empty
    if (!category || !level || !division) {
      try {
        const submission = routine.parentRoutine?.submissionRoutines?.[0];
        category = category || submission?.category?.name || '';
        level = level || submission?.level?.name || '';
        division = division || submission?.division?.name || '';
      } catch (error) {
        // Keep existing values or empty strings
      }
    }

    // Combine in the correct order: Level (Age Group) → Category → Division
    // Example: "Junior Musical Theatre Solo"
    const parts = [level, category, division].filter(part => part && part !== 'Unknown');
    return parts.length > 0 ? parts.join(' ') : 'Unknown Category';
  };

  const getRoutineInfo = (routine: any) => {
    // Get extraTime and format it properly
    const extraTimeValue = routine.extraTime || '0';
    const extraTimeFormatted = extraTimeValue === '0' || !extraTimeValue ? 'No' : `${extraTimeValue} min`;
    
    return {
      ageUp: routine.ageUp || false,
      extraTime: extraTimeFormatted,
      routineType: routine.routineType || 'routine',
      choreographer: routine.choreographer || 'Not available',
      averageAge: routine.averageAge || null
    };
  };

  const getDancerCount = (routine: any) => {
    // First check if dancersCount is already calculated (from our transform function)
    if (routine.dancersCount && routine.dancersCount !== 1) {
      return routine.dancersCount;
    }
    
    // SMART FALLBACK: Use division to estimate dancer count for HitchKick data
    if (routine.division) {
      const division = routine.division.toLowerCase();
      
      if (division.includes('solo')) {
        return 1;
      } else if (division.includes('duo') || division.includes('duet')) {
        return 2;
      } else if (division.includes('trio')) {
        return 3;
      } else if (division.includes('small group')) {
        // Small groups typically range from 4-9, use 7 as better average
        return 7;
      } else if (division.includes('large group') || division.includes('group')) {
        return 12; // Typical large group size
      } else if (division.includes('line')) {
        return 18; // Typical line size
      } else if (division.includes('production')) {
        return 25; // Typical production size
      }
    }
    
    // Try to get from HitchKick data structure
    const hitchkickDancers = routine.parentRoutine?.submissionRoutines?.[0]?.routineDancers;
    if (hitchkickDancers && hitchkickDancers.length > 0) {
      return hitchkickDancers.length;
    }
    
    // Try original enhanced data structure
    const originalDancers = routine.parentRoutine?.routineDancers;
    if (originalDancers && originalDancers.length > 0) {
      return originalDancers.length;
    }
    
    // Check if we have originalData preserved from transform
    const originalData = routine.originalData;
    if (originalData?.parentRoutine?.submissionRoutines?.[0]?.routineDancers) {
      const originalHitchkickDancers = originalData.parentRoutine.submissionRoutines[0].routineDancers;
      return originalHitchkickDancers.length;
    }
    
    // If no dancers found, return 0 instead of 1
    return 0;
  };

  const formatScheduledDateTime = (routine: any, eventTimezone: string | null = null) => {
    const timeString = routine.startTime;
    if (!timeString) return { date: 'Not scheduled', time: 'Not scheduled' };
    
    try {
      const date = new Date(timeString);
      const dateOptions: Intl.DateTimeFormatOptions = {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      };
      const timeOptions: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      };
      
      if (eventTimezone) {
        dateOptions.timeZone = eventTimezone;
        timeOptions.timeZone = eventTimezone;
      }
      
      const formattedDate = date.toLocaleDateString('en-US', dateOptions);
      const formattedTime = date.toLocaleTimeString('en-US', timeOptions);
      return { date: formattedDate, time: formattedTime };
    } catch (error) {
      return { date: 'Invalid date', time: 'Invalid time' };
    }
  };

  const formatDuration = (routine: any) => {
    if (routine.duration) return routine.duration;
    if (routine.startTime && routine.endTime) {
      try {
        const start = new Date(routine.startTime);
        const end = new Date(routine.endTime);
        const diffMs = end.getTime() - start.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffSecs = Math.floor((diffMs % 60000) / 1000);
        return `${diffMins}:${diffSecs.toString().padStart(2, '0')}`;
      } catch (error) {
        return 'Unknown';
      }
    }
    return 'Unknown';
  };

  const performerName = getPerformerName(currentRoutine);
  const studioName = getStudioName(currentRoutine);
  const studioCode = getStudioCode(currentRoutine);
  const combinedCategory = getCombinedCategoryInfo(currentRoutine);
  const routineInfo = getRoutineInfo(currentRoutine);
  const dancerCount = getDancerCount(currentRoutine);
  const scheduledDateTime = formatScheduledDateTime(currentRoutine, eventTimezone);
  const isScratched = currentRoutine?.scratched || false;

  return (
    <div className={`${className} flex flex-col h-full`}>
      {/* Compact Header with Date/Time */}
      <div className="flex justify-between items-start mb-2 flex-shrink-0">
        <CardTitle className={`flex items-center ${universalConfig.spacing}`}>
          <Trophy className={`${universalConfig.iconSize} text-purple-400`} />
          ROUTINE METADATA
        </CardTitle>
        
        {/* Date/Time in top-right */}
        <div className="text-right text-sm text-gray-400 leading-tight">
          <div className="font-medium">
            {scheduledDateTime.date} • {scheduledDateTime.time}
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-grow overflow-hidden">
        <div className="h-full overflow-y-auto space-y-3 pb-0">
          {/* Main Info - Responsive Grid with Improved Spacing */}
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 relative">
            {/* Scratch Button - Top Right Corner of Nested Card */}
            {onScratchRoutine && onUnscratchRoutine && (
              <div className="absolute top-3 right-3 z-10">
                {!isScratched ? (
                  <button
                    onClick={onScratchRoutine}
                    disabled={!currentRoutine}
                    className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-red-500/30 to-pink-500/30 border border-red-400/50 text-red-200 rounded-lg hover:from-red-500/40 hover:to-pink-500/40 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Scratch this routine"
                  >
                    <Ban className="w-3 h-3" />
                    <span className="text-xs font-medium">Scratch</span>
                  </button>
                ) : (
                  <button
                    onClick={onUnscratchRoutine}
                    disabled={!currentRoutine}
                    className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-green-500/30 to-emerald-500/30 border border-green-400/50 text-green-200 rounded-lg hover:from-green-500/40 hover:to-emerald-500/40 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Unscratch this routine"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span className="text-xs font-medium">Undo</span>
                  </button>
                )}
              </div>
            )}
            
            {/* Routine Title - Inside the card with reduced spacing */}
            <div className="mb-3 pb-3 border-b border-white/[0.08] pr-20">
              <div className={`text-sm font-bold ${isScratched ? 'text-red-400' : 'text-white'}`}>
                <span className="text-purple-300">#</span><span className="text-pink-300">{currentRoutine.routineNumber || 'N/A'}</span> 
                <span className="text-gray-300 mx-1">-</span> 
                <span className={`font-black ${isScratched ? 'text-red-300' : 'text-white'}`}>{currentRoutine.name || 'Untitled Routine'}</span>
                {performerName && performerName !== 'Unknown Performer' && (
                  <span className={`font-medium ml-1 ${isScratched ? 'text-red-200' : 'text-orange-300'}`}>
                    ({performerName})
                  </span>
                )}
                {isScratched && (
                  <span className="text-xs bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded border border-red-400/40 ml-2">
                    SCRATCHED
                  </span>
                )}
              </div>
            </div>
            
            <div className="space-y-3">
              {/* Row 1: Studio and Type */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div className="flex items-start gap-2">
                  <Building className="w-3 h-3 mt-[2px] text-purple-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium uppercase tracking-[0.05em] text-gray-400">
                      STUDIO
                    </p>
                    <div className="h-8 flex items-start">
                      <p className={`text-xs font-medium leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {studioName}{studioCode ? ` (${studioCode})` : ''}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <Music className="w-3 h-3 mt-[2px] text-green-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium uppercase tracking-[0.05em] text-gray-400">
                      TYPE
                    </p>
                    <p className={`text-xs font-medium capitalize truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {routineInfo.routineType}
                    </p>
                  </div>
                </div>
              </div>

              {/* Row 2: Choreographer and Dancers */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div className="flex items-start gap-2">
                  <User className="w-3 h-3 mt-[2px] text-yellow-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium uppercase tracking-[0.05em] text-gray-400">
                      CHOREOGRAPHER
                    </p>
                    <div className="h-8 flex items-start">
                      <p className={`text-xs font-medium leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {routineInfo.choreographer === 'Not available' ? (
                          <span className="text-gray-500 italic">N/A</span>
                        ) : (
                          routineInfo.choreographer
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <Users className="w-3 h-3 mt-[2px] text-orange-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium uppercase tracking-[0.05em] text-gray-400">
                      DANCERS
                    </p>
                    <p className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {dancerCount}
                    </p>
                  </div>
                </div>
              </div>

              {/* Row 3: Category and Age Up/Extra */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div className="flex items-start gap-2">
                  <Tag className="w-3 h-3 mt-[2px] text-blue-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium uppercase tracking-[0.05em] text-gray-400">
                      CATEGORY
                    </p>
                    <div className="h-8 flex items-start">
                      <p className={`text-xs font-medium leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {combinedCategory}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="min-w-0 flex-1">
                  {/* Age Up and Extra Time in horizontal layout */}
                  <div className="grid grid-cols-2 gap-6">
                    <div className="flex items-start gap-2">
                      <Star className="w-3 h-3 mt-[2px] text-yellow-400 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium uppercase tracking-[0.05em] text-gray-400">
                          AGE UP
                        </p>
                        <div className="flex items-center gap-1">
                          {routineInfo.ageUp ? (
                            <span className="text-green-400 text-xs">✓</span>
                          ) : (
                            <span className="text-red-400 text-xs">✗</span>
                          )}
                          <span className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {routineInfo.ageUp ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2">
                      <Clock className="w-3 h-3 mt-[2px] text-orange-400 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium uppercase tracking-[0.05em] text-gray-400">
                          EXTRA
                        </p>
                        <div className="flex items-center gap-1">
                          {routineInfo.extraTime === 'No' ? (
                            <span className="text-red-400 text-xs">✗</span>
                          ) : (
                            <span className="text-green-400 text-xs">✓</span>
                          )}
                          <span className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {routineInfo.extraTime === 'No' ? 'No' : routineInfo.extraTime}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Additional Info - Only if available and space permits */}
          {(routineInfo.averageAge || getSubmissionRoutineTitle(currentRoutine)) && (
            <div className="bg-white/5 border border-white/10 rounded-lg p-3">
              <h4 className="text-xs font-medium uppercase tracking-wide text-gray-400 mb-2">
                ADDITIONAL
              </h4>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {routineInfo.averageAge && (
                  <div className="flex items-start gap-2">
                    <Users className="w-3 h-3 mt-0.5 text-blue-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                        AVG AGE
                      </p>
                      <p className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {routineInfo.averageAge}y
                      </p>
                    </div>
                  </div>
                )}

                {getSubmissionRoutineTitle(currentRoutine) && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-3 h-3 mt-0.5 text-purple-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                        TITLE
                      </p>
                      <p className={`text-xs font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {getSubmissionRoutineTitle(currentRoutine)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoutineMetadataCard;
